import { Component, ComponentRef, OnDestroy, OnInit, ComponentFactoryResolver, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentCollectionService } from '../../service/payment-collection.service';
import { Keys, StorageService } from '../../service/storage.service';
import { PaymentCollectionRequest, PaymentMode, PaymentProduct, TabChangeCallback } from '../../model/payment/PaymentCollectionRequest';
import { MatDialog } from '@angular/material/dialog';
import { DiscardChangesPromptComponent } from '../../discard-changes-prompt/discard-changes-prompt.component';
import { FormGroup } from '@angular/forms';
import { BadRequestError } from '../../errors/BadRequestError';
import { Toaster, Type } from '../../toaster/Toaster';
import { EncryptUtil } from '../../../app/utils/encrypt.util';
import { NotFoundError } from '../../errors/NotFoundError';
import { InternalServerError } from '../../errors/InternalServerError';
import { FfEndPoints } from '../../views/investors/finzy-flex/ff-endpoints';
import { SharedService } from '../../service/shared.service';
import { LoanDetailsService } from '../../service/loan-details.service';
import { Loan } from '../../model/loan.model';
import { ConfigService } from '../../service/config.service';
import * as atob from 'atob';

interface PaymentInitiator {
  link: string;
  linkLabel: string;
  label: string;
  userType: string
}

@Component({
  selector: 'app-initiate-payment',
  templateUrl: './initiate-payment.component.html',
  styleUrls: ['./../../../assets/styles/finzy-styles.scss', './initiate-payment.component.scss']
})

export class InitiatePaymentComponent implements OnInit, OnDestroy {

  public dataLoader: boolean = false;
  paymentInitiator: PaymentInitiator;
  paymentCollectionRequest: PaymentCollectionRequest;
  private readonly matDialog: MatDialog;
  private encryptUtil: EncryptUtil;

  inputContent: string;
  private componentRef: ComponentRef<any>;
  private amount: number;
  private loanId: string;
  private paymentProduct: PaymentProduct;
  private router: Router;
  private storageService: StorageService;
  private componentFactoryResolver: ComponentFactoryResolver
  private paymentCollectionService: PaymentCollectionService;
  public currentFormRef: FormGroup;
  disableAmountField: boolean = false;
  /**
   * showLoader: By default the loader will show. 
   * It will not show in following scenerios 
   * Normal payment: once payment product is created
   * IFA payment: once verifyPaymentLink() is called and response is recieved
   * QUID payment: once oncy autoSignIn() is either complete(payment page) or failed(invalid page)
   */
  showLoader: boolean = true;

  public isPaymentLinkInValid: boolean = false;

  public rowOne = {
    failureLogo: "../../../assets/images/payment-collection/payment_failed.svg",
    failureMsg: "Invalid link",
    failureInfoMsg: ''
  };

  public templateButtonText = 'Login';

  @ViewChild('paymentContainer', { read: ViewContainerRef }) paymentContainer!: ViewContainerRef;
  public currentTabDetail: TabChangeCallback;
  showTemplateButton = true;
  constructor(
    router: Router,
    matDialog: MatDialog,
    storageService: StorageService,
    encryptUtil: EncryptUtil,
    componentFactoryResolver: ComponentFactoryResolver,
    paymentCollectionService: PaymentCollectionService,
    private sharedService: SharedService,
    private route: ActivatedRoute,
    private readonly loanDetailsService: LoanDetailsService,
    private readonly configService: ConfigService
  ) {

    this.router = router;
    this.storageService = storageService;
    this.matDialog = matDialog;
    this.encryptUtil = encryptUtil;
    this.componentFactoryResolver = componentFactoryResolver;
    this.paymentCollectionService = paymentCollectionService;
    // Handling payment link URL
    this.handleIFAPartnerPaymentLink();
  }

  async ngOnInit() {
  }

  private getAmount(): number {
    if (this.amount) {
      this.storageService.saveItem('amount', this.amount.toString());
      return this.amount;
    }
    return parseFloat(this.storageService.getItem('amount'));
  }

  private getLoanId(): string {
    if (this.loanId) {
      this.storageService.saveItem(Keys.GENERATED_LOAN_ID, this.loanId.toString());
      return this.loanId;
    }
    return this.storageService.getItem(Keys.GENERATED_LOAN_ID);
  }

  private getPaymentProduct(): PaymentProduct {
    if (this.paymentProduct) {
      this.storageService.saveItem('paymentProduct', this.paymentProduct);
      return this.paymentProduct;
    }
    if (this.storageService.getItem('paymentProduct') === PaymentProduct.UPDATE_LENDER_WALLET.toString()) {
      return PaymentProduct.UPDATE_LENDER_WALLET;
    } else if (this.storageService.getItem('paymentProduct') === PaymentProduct.LOAN_EMI_PAYMENT.toString()) {
      return PaymentProduct.LOAN_EMI_PAYMENT;
    } else {
      this.router.navigate(['investorwallet']);
    }
  }

  async pay(): Promise<void> {
    this.storageService.saveItem('amount', (this.paymentCollectionRequest.amount).toString());
    this.currentTabDetail.currentFormInstance.markAllAsTouched();
    if (this.currentTabDetail.currentFormInstance.valid && this.paymentCollectionRequest.amount) {
      this.dataLoader = true;
      if (this.paymentCollectionRequest.paymentMode === PaymentMode.DEBIT_CARD_SAVED) {
        this.paymentCollectionRequest.paymentMode = PaymentMode.DEBIT_CARD;
      }
      const encryptedData = this.encryptUtil.getEncryptedData(JSON.stringify(this.paymentCollectionRequest));
      if (PaymentProduct.UPDATE_LENDER_WALLET === this.paymentCollectionRequest.paymentProduct) {
        const uuid: string = this.storageService.getItem(Keys.IFA_PAYMENT_LINK_UUID);
        const hashedProfileId: string = this.storageService.getItem(Keys.HASHED_PROFILE_ID);
        if (uuid) {
          this.IFAPayment(uuid, encryptedData, hashedProfileId)
        } else {
          this.lenderPayment(encryptedData)
        }
      } else if (PaymentProduct.LOAN_EMI_PAYMENT === this.paymentCollectionRequest.paymentProduct) {
        const generatedLoanId: string = this.storageService.getItem(Keys.GENERATED_LOAN_ID);
        if (generatedLoanId) {
          this.paymentCollectionRequest.generatedLoanId = generatedLoanId;
          console.log("loan payment", this.paymentCollectionRequest);
          this.borrowerPayment(encryptedData);
        } else {
          this.borrowerPayment(encryptedData)
        }
      } else {
        Toaster.toast("Something went wrong: Invalid product", Type.Error);
      }
    }
  }

  // borrower payment
  async borrowerPayment(encryptedData) {
    await this.paymentCollectionService.collectBorrowerPayment(encryptedData)
      .then(paymentCollectionResponse => {
        this.inputContent = this.encryptUtil.decodeBase64(paymentCollectionResponse.result.acsTemplate);
        this.dataLoader = false;
        this.createComponentFromRaw(this.inputContent);
      }).catch(error => {
        if (error instanceof BadRequestError || error instanceof InternalServerError) {
          Toaster.toast(error.message, Type.Error);
        } else if (error.status === NotFoundError.statusCode) {
          Toaster.toast(error.error.message, Type.Error);
        } else {
          Toaster.toast("Something went wrong, please try again", Type.Error);
        }
        this.dataLoader = false;
      })
  }

  // lender payment
  async lenderPayment(encryptedData) {
    await this.paymentCollectionService.collectPayment(encryptedData)
      .then(paymentCollectionResponse => {
        this.inputContent = this.encryptUtil.decodeBase64(paymentCollectionResponse.result.acsTemplate);
        this.dataLoader = false;
        this.createComponentFromRaw(this.inputContent);
      }).catch(error => {
        if (error instanceof BadRequestError || error instanceof InternalServerError) {
          Toaster.toast(error.message, Type.Error);
        } else if (error.status === NotFoundError.statusCode) {
          Toaster.toast(error.error.message, Type.Error);
        } else {
          Toaster.toast("Something went wrong, please try again", Type.Error);
        }
        this.dataLoader = false;
      })
  }

  // IFA link payment
  async IFAPayment(uuid, encryptedData, token) {
    if (PaymentProduct.UPDATE_LENDER_WALLET === this.paymentCollectionRequest.paymentProduct) {
      await this.paymentCollectionService.collectPaymentIFA(uuid, encryptedData, token)
        .then(paymentCollectionResponse => {
          this.inputContent = this.encryptUtil.decodeBase64(paymentCollectionResponse.result.acsTemplate);
          this.dataLoader = false;
          this.createComponentFromRaw(this.inputContent);
        }).catch(error => {
          if (error instanceof BadRequestError || error instanceof InternalServerError) {
            Toaster.toast(error.message, Type.Error);
          } else if (error.status === NotFoundError.statusCode) {
            Toaster.toast(error.error.message, Type.Error);
          } else {
            Toaster.toast("Something went wrong, please try again", Type.Error);
          }
          this.dataLoader = false;
        })
    }
  }

  private createComponentFromRaw(template: string) {
    this.destroyPaymentComponent();
    function TmpCmpConstructor() {
    }
    const tmpCmp = Component({ template })(new TmpCmpConstructor().constructor);
    const dynamicComponentFactory = this.componentFactoryResolver.resolveComponentFactory(tmpCmp);
    const componentRef = this.paymentContainer.createComponent(dynamicComponentFactory);
    this.paymentContainer.insert(componentRef.hostView);
    const form = (<HTMLFormElement>document.getElementById('payment_post'));
    form.submit();
  }
  ngOnDestroy() {
    this.destroyPaymentComponent();
  }

  private destroyPaymentComponent() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  async cancelPayment() {
    const dialogRef = this.matDialog.open(DiscardChangesPromptComponent);
    const dialogResult = await dialogRef.afterClosed().toPromise();
    const pathToRedirect = this.storageService.getItem(Keys.PARENT_WALLET_PATH);
    if (dialogResult) {
      if (pathToRedirect) {
        await this.router.navigate([pathToRedirect]);
        this.storageService.removeItem(Keys.PARENT_WALLET_PATH);
      } else {
        await this.router.navigate([this.paymentInitiator.link]);
      }
    }
  }

  private buildPaymentInitiator() {
    if (PaymentProduct.UPDATE_LENDER_WALLET === this.paymentCollectionRequest.paymentProduct) {
      const parentWalletPath = this.storageService.getItem(Keys.PARENT_WALLET_PATH);
      if (parentWalletPath == '/investment-initial') {
        this.paymentInitiator = {
          link: `/investment-initial`,
          linkLabel: 'Wallet',
          label: 'Add to wallet',
          userType: 'Lender'
        };
      } else {
        this.paymentInitiator = {
          link: parentWalletPath?.includes(FfEndPoints.FINZY_FLEX) ? `/${FfEndPoints.FINZY_FLEX}` : '/investorwallet',
          linkLabel: 'Wallet',
          label: 'Add to wallet',
          userType: 'Lender'
        };
      }
    } else if (PaymentProduct.LOAN_EMI_PAYMENT === this.paymentCollectionRequest.paymentProduct) {
      this.paymentInitiator = {
        link: `/borrow/dashboard/applications`,
        linkLabel: 'PREPAY / PAYEMI',
        label: 'Amount to Pay',
        userType: 'Borrower'
      };
    } else {
      return;
    }
  }

  public onTabChanged(currentTabDetail: TabChangeCallback) {
    this.currentTabDetail = currentTabDetail;
    this.currentTabDetail.currentFormInstance.valueChanges.subscribe(value => {
    });
  }

  // Handling payment link URL
  private handleIFAPartnerPaymentLink() {
    // 1. extraction queryParams from URL
    this.route.queryParams.subscribe(async (params) => {
      const { loanId, amount, successRedirectURL, failedRedirectURL, referenceId } = params;
      if (referenceId) {
        this.attemptAutoSignIn(referenceId);
      } else if (loanId && amount) {
        this.disableAmountField = true;
        this.storageService.saveItem(Keys.PAYMENT_SUCCESS_REDIRECT_URL, successRedirectURL);
        this.storageService.saveItem(Keys.PAYMENT_FAILED_REDIRECT_URL, failedRedirectURL);
        await this.router.navigate(['/payment/initiate'], { state: { topUpAmount: amount, paymentProduct: PaymentProduct.LOAN_EMI_PAYMENT, loanId: loanId } });
      } else {
        // 2. This step when link is entered first time
        const { uuid, token, amount } = params;
        // 3. This step is for when payment failure and try again
        const isUserPaymentLinkFailed: boolean = this.isUserComingFromPaymentLinkFailed();
        const transactionStatus = this.storageService.getItem(Keys.TRANSACTION_STATUS);

        if (uuid && token && amount) {
          // verify payment link when payment link is valid
          await this.verifyPaymentLink(uuid, token, amount)
        } else if (isUserPaymentLinkFailed && transactionStatus) {
          // verify payment link when payment got failed
          const hashedProfileId = this.storageService.getItem(Keys.HASHED_PROFILE_ID);
          const ifaPaymentLinkUuid = this.storageService.getItem(Keys.IFA_PAYMENT_LINK_UUID);
          this.verifyPaymentLink(ifaPaymentLinkUuid, hashedProfileId)
        } else {
          this.paymentProduct = this.router.getCurrentNavigation().extras.state?.paymentProduct;
          this.amount = this.router.getCurrentNavigation().extras.state?.topUpAmount;
          this.loanId = this.router.getCurrentNavigation().extras.state?.loanId;
          this.paymentCollectionRequest = {
            userId: this.storageService.getItem(Keys.PROFILE_ID),
            paymentProduct: this.getPaymentProduct(),
            amount: this.getAmount(),
            paymentMode: PaymentMode.DEBIT_CARD_SAVED,
            generatedLoanId: this.getLoanId()
          };
          this.buildPaymentInitiator();
          this.showLoader = false;
        }
      }
    });
  }

  private attemptAutoSignIn(referenceId: string): void {
    try {
      this.storageService.saveItem(Keys.PAYMENT_REFERENCE_ID, referenceId);
      this.storageService.saveItem(Keys.COMING_FROM_RETAILER_PARTNER_PAYMENT_LINK, 'true');
      this.storageService.removeItem(Keys.RETAILER_PARTNER_LINK_VALIDATED);
      // Integrate tha API
      this.paymentCollectionService.getBorrowerPaymentLinkDetails(referenceId)
        .then(async (res) => {
          if (res) {
            const { loanId, token, amount, successRedirectURL, failedRedirectURL } = res;
            const jwtPayload = token.split('.')[1];
            const jwtPayloadBase64 = jwtPayload.replace('-', '+').replace('_', '/');
            const jwtPayloadJson = JSON.parse(atob(jwtPayloadBase64));
            this.sharedService.setAutoSignInValue(true);
            this.storageService.saveItem(Keys.TOKEN, token);
            this.storageService.saveItem(Keys.PROFILE_ID, jwtPayloadJson['sub']);
            this.storageService.saveItem(Keys.RETAILER_PARTNER_LINK_VALIDATED, 'true');
            this.getLoanPartnerName(loanId, amount, successRedirectURL, failedRedirectURL);
          }
        })
        .catch((error) => {
          console.log(error);
          this.isPaymentLinkInValid = true;
          this.showLoader = false;
          this.showTemplateButton = false;
          this.rowOne.failureInfoMsg = 'Payment link is invalid, please try again';
          Toaster.toast("Payment link is invalid, please try again", Type.Error);
        });
    } catch(e) {
      this.storageService.clear();
      this.router.navigate(['/borrow']);
    }
  }


  private getLoanPartnerName(loanId: string, amount: number, successRedirectURL: string, failedRedirectURL: string): void {
    this.storageService.removeItem(Keys.RETAILER_PARTNER_LINK_VALIDATED);
    this.loanDetailsService.getPartnerByGeneratedLoanId(loanId)
      .then((res) => {
        const partner: { partnerName: string } = res;

        if (partner?.partnerName) {
          const isElementPresent: boolean = this.configService.getRetailerPartnersToAvoidLoginPage().some(element => partner.partnerName.toLowerCase().includes(element.toLowerCase()));
          console.log("getRetailerPartnersToAvoidLoginPage : ", this.configService.getRetailerPartnersToAvoidLoginPage());
          if (isElementPresent) {
            this.storageService.saveItem(Keys.RETAILER_PARTNER_LINK_VALIDATED, 'true');
          }
        };
        this.sharedService.setAutoSignInValue(true);
        this.router.navigate(['/payment/initiate'],
          {
            queryParams: { loanId, amount, successRedirectURL, failedRedirectURL }
          });
        this.showLoader = false;
      })
      .catch((error) => {
        console.log(error)
      })
  }

  isLoggedInUser() {
    const profileId = this.storageService.getItem(Keys.PROFILE_ID);
    const paymentProduct = this.paymentProduct || this.storageService.getItem('paymentProduct');
    if (profileId && paymentProduct) {
      return true;
    }
    return false;
  }

  verifyPaymentLink(uuid, token, amount?: string) {
    const amountFromLocalStorage = this.storageService.getItem('amount') ?? amount;

    this.storageService.saveItem(Keys.HASHED_PROFILE_ID, token);
    this.paymentCollectionService.verifyPaymentLinkOfIFA(uuid, token)
      .then(async (res) => {
        this.showLoader = false;
        if (res.profileId) {
          this.storageService.saveItem(Keys.PROFILE_ID, res.profileId);
          this.storageService.saveItem(Keys.IFA_PAYMENT_LINK_UUID, uuid);
          this.isPaymentLinkInValid = false;
          this.storageService.removeItem(Keys.TRANSACTION_STATUS);
          await this.router.navigate(['link-payments'], { state: { topUpAmount: amountFromLocalStorage, paymentProduct: PaymentProduct.UPDATE_LENDER_WALLET } });
        }
      }).catch(error => {
        this.showLoader = false;
        this.isPaymentLinkInValid = true;
        this.storageService.removeItem(Keys.PROFILE_ID);
        this.storageService.removeItem(Keys.IFA_PAYMENT_LINK_UUID);
        this.storageService.removeItem(Keys.TRANSACTION_STATUS);
        this.storageService.removeItem(Keys.HASHED_PROFILE_ID);

        if (error instanceof BadRequestError || error instanceof InternalServerError) {
          Toaster.toast(error.message, Type.Error);
          this.rowOne.failureInfoMsg = error.message;
        } else if (error.status === NotFoundError.statusCode) {
          Toaster.toast(error.error.message, Type.Error);
          this.rowOne.failureInfoMsg = error.error.message;
        } else {
          this.rowOne.failureInfoMsg = 'Something went wrong, please try again';
          Toaster.toast("Something went wrong, please try again", Type.Error);
        }
      })
  }

  isUserComingFromPaymentLinkFailed() {
    const hashedProfileId = this.storageService.getItem(Keys.HASHED_PROFILE_ID);
    const ifaPaymentLinkUuid = this.storageService.getItem(Keys.IFA_PAYMENT_LINK_UUID);
    const profileId = this.storageService.getItem(Keys.PROFILE_ID);
    if (hashedProfileId && ifaPaymentLinkUuid && profileId) {
      return true;
    };
    return false;
  }

  isLodaded() {
    const paymentReferenceId = this.storageService.getItem(Keys.PAYMENT_REFERENCE_ID);
    if (paymentReferenceId) {
      const payment_success_redirect_url = this.storageService.getItem(Keys.PAYMENT_SUCCESS_REDIRECT_URL);
      const payment_failed_redirect_url = this.storageService.getItem(Keys.PAYMENT_FAILED_REDIRECT_URL);
      if (payment_success_redirect_url && payment_failed_redirect_url) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }
}
